import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { OrdrWebSocketClient } from '../utils/OrdrWebSocketClient';
import { createKdsTasksCacheSync } from './kdsTasksCacheSync';
import { useOrdersWebSocketStore } from './OrdersWebSocketStore';
import useDeviceId from '../hooks/useDeviceId';

type OrdrWebSocketContextValue = {
  setStations: (stationIds: number[]) => void;
};

const OrdrWebSocketContext = createContext<OrdrWebSocketContextValue | null>(null);

export const OrdrWebSocketProvider = ({ locationId, children }: { locationId: number; children: React.ReactNode }) => {
  const { getAccessToken } = useAuth();
  const deviceId = useDeviceId();
  const queryClient = useQueryClient();

  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  const clientRef = useRef<OrdrWebSocketClient | null>(null);
  // Desired stations outlive the client, so a provider remount (e.g. location
  // change) carries the subscriptions onto the fresh client.
  const stationsRef = useRef<number[]>([]);

  useEffect(() => {
    // Pure wiring: connection state and the debug log go to the zustand store,
    // task messages go to the react-query cache sync. The client itself knows
    // nothing about either.
    const cacheSync = createKdsTasksCacheSync(queryClient, locationId);
    const client = new OrdrWebSocketClient({
      getToken: () => getAccessTokenRef.current(),
      deviceId,
      onStateChange: (state) => useOrdersWebSocketStore.getState().setConnectionState(state),
      onMessageLog: (msg) => useOrdersWebSocketStore.getState().addMessage(msg),
      onMessage: cacheSync.onMessage,
    });
    clientRef.current = client;
    client.setStations(stationsRef.current);
    client.connect();

    return () => {
      cacheSync.dispose();
      client.close();
      clientRef.current = null;
      useOrdersWebSocketStore.getState().reset();
    };
  }, [deviceId, queryClient, locationId]);

  const setStations = useCallback((stationIds: number[]) => {
    stationsRef.current = stationIds;
    clientRef.current?.setStations(stationIds);
  }, []);

  const value = useMemo(() => ({ setStations }), [setStations]);
  return <OrdrWebSocketContext.Provider value={value}>{children}</OrdrWebSocketContext.Provider>;
};

export function useOrdrWebSocket(): OrdrWebSocketContextValue {
  const ctx = useContext(OrdrWebSocketContext);
  if (!ctx) throw new Error('useOrdrWebSocket must be used within OrdrWebSocketProvider');
  return ctx;
}
